<template>
    <app-layout class="practice-ancillary-tests">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>
            </common-content-spacer>

            <common-page-plain>
                <common-content-spacer height="medium">
                    <div class="practice-ancillary-tests--header">
                        <common-icon name="ancillary-diagnostic" size="gigantic"></common-icon>
                        <p class="practice-ancillary-tests--header--text">{{ pageSubTitle }}</p>
                    </div>
                    <div>
                        <common-content-wrapper width="small" :isCenter="false">
                            <select-input
                                class="schedule-apply__input"
                                :placeholder="$t('forms.auth.schedule.fields.location.placeholder')"
                                :hasAutofocus="true"
                                :options="locationOptions"
                                :value="activeLocationId"
                                @change="changeCurrentLocationId($event)"
                            ></select-input>
                        </common-content-wrapper>
                    </div>

                    <ancillary-service-tests-view
                        :ancillaryServiceData="ancillaryServiceData"
                        :prefillValues="checkedSubgroups"
                        @ancillary-tests-selection-save="saveChanges"
                        @ancillary-tests-selection-changed="selectionChanged"
                        @change-location="changeLocation"
                    ></ancillary-service-tests-view>
                </common-content-spacer>
            </common-page-plain>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import FacilityServicesAPI from 'api/FacilityServicesAPI'
    import AncillaryServiceTestsView from 'components/page-elements/ancillary-service-tests/AncillaryServiceTestsView'
    import SelectInput from 'components/form-inputs/SelectInput'
    import cloneDeep from 'lodash.clonedeep'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin],

        components: {
            AppLayout,
            AncillaryServiceTestsView,
            SelectInput,
        },

        data() {
            return {
                ancillaryService: [],
                isLoading: false,
                header: '',
                locationOptions: [],
                activeLocationId: 'all',
                ancillaryServiceData: {},
                serviceSubCategoriesByLocationData: [],
                checkedSubgroups: [],
            }
        },

        computed: {
            pageTitle() {
                return this.$t('pages.practice.profile.titles.practice_profile', { practiceName: this.$auth.getPractice().name })
            },

            pageSubTitle() {
                return this.$auth
                    .getFacilityServicesByLocation()
                    .find((el) => Number(el.facilityServiceTypeId) === Number(this.$route.params.serviceId))
                    .facilityServiceTypeName
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.practice.insurance.breadcrumbs.practice'),
                        route: { name: 'practice.members' },
                    },
                    {
                        label: this.$t('pages.practice.profile.breadcrumbs.practice_profile'),
                        route: { name: 'practice.profile-ancillary' },
                    },
                    {
                        label: this.$t('pages.practice.profile.breadcrumbs.practice_tests_list'),
                        route: { name: 'practice.service.tests', params: { serviceId: this.$route.params.serviceId } },
                    },
                    {
                        label: this.$t('pages.practice.profile.breadcrumbs.practice_tests'),
                    },
                ]
            },
        },

        methods: {
            getAllServiceTests() {
                FacilityServicesAPI.getFacilityServiceSubGroups(this.$route.params.serviceId).then((response) => {
                    this.ancillaryServiceData = response
                    this.getServiceTestsByLocation()
                })
            },

            changeCurrentLocationId(locationId) {
                this.activeLocationId = locationId
                this.checkedValuesChanged()
            },

            changeLocation() {
                const currentIndex = this.locationOptions.map((item) => item.value).indexOf(this.activeLocationId)

                if (currentIndex === this.locationOptions.length - 1 || this.activeLocationId === 'all') {
                    this.saveChanges(true)
                }

                if (currentIndex < this.locationOptions.length - 1) {
                    this.changeCurrentLocationId(this.locationOptions[currentIndex + 1].value)
                }
            },

            getServiceTestsByLocation() {
                FacilityServicesAPI.getServiceTests(this.$auth.getPractice().id, this.$route.params.serviceId).then(
                    (response) => {
                        response.forEach((item) => {
                            item.facilityServiceTypesSubgroups = item.facilityServiceTypesSubgroups.map((el) => el.id)
                            this.serviceSubCategoriesByLocationData.push(item)
                        })
                        this.locationOptions = response.map((location) => ({
                            label: location.locationName,
                            value: location.practiceLocationId.toString(),
                        }))

                        if (this.$route.params.locationId.length || this.$route.params.locationId === 0) {
                            this.activeLocationId = this.$route.params.locationId
                        }
                        this.checkIsAllLocationAvailable()
                        this.checkedValuesChanged()
                    },
                )
            },

            checkIsAllLocationAvailable() {
                let allTestsLength = 0
                this.serviceSubCategoriesByLocationData.forEach((item) => {
                    allTestsLength += item.facilityServiceTypesSubgroups.length
                })
                if (!allTestsLength) {
                    this.locationOptions.unshift({
                        label: this.$t('forms.auth.schedule.microcopy.all_locations'),
                        value: 'all',
                    })
                }
            },

            checkedValuesChanged() {
                this.checkedSubgroups = cloneDeep(
                    this.serviceSubCategoriesByLocationData.find(
                        (el) => el.practiceLocationId.toString() === this.activeLocationId,
                    )?.facilityServiceTypesSubgroups,
                )
            },

            selectionChanged(value) {
                if (this.activeLocationId !== 'all') {
                    this.serviceSubCategoriesByLocationData.find(
                        (el) => el.practiceLocationId.toString() === this.activeLocationId,
                    ).facilityServiceTypesSubgroups = value
                    this.removeAllLocation()
                } else {
                    this.serviceSubCategoriesByLocationData.forEach((element) => {
                        element.facilityServiceTypesSubgroups = value
                    })
                }
            },

            removeAllLocation() {
                this.locationOptions = this.locationOptions.filter((el) => el.value !== 'all')
            },

            saveChanges(goToServiceTestSelection) {
                const facilityServiceSubgroups = this.serviceSubCategoriesByLocationData.map((item) => ({
                    locationId: item.practiceLocationId === 'main' ? null : item.practiceLocationId,
                    subgroupIds: item.facilityServiceTypesSubgroups,
                }))

                FacilityServicesAPI.updateFacilityServiceTests(this.$auth.getPractice().id, this.$route.params.serviceId, {
                    facilityServiceSubgroups,
                }).then(() => {
                    this.$auth.updateFacilityServicesByLocation()

                    if (!goToServiceTestSelection) {
                        this.$router.push({ name: 'practice.profile-ancillary' })
                        return
                    }

                    this.$router.push({
                        name: 'practice.service.tests',
                        params: { serviceId: this.$route.params.serviceId },
                        query: { advance: this.$route.query.advance ? this.$route.query.advance : false },
                    })
                })
            },
        },

        mounted() {
            this.getAllServiceTests()
        },
    }
</script>

<style lang="scss" scoped>
    @import 'styles/setup/_variables.scss';

    .practice-ancillary-tests {
        &--header {
            display: flex;
            align-items: center;
            &--text {
                font-weight: 500;
                font-size: 20px;
                line-height: 30px;
                letter-spacing: 0.325px;
                color: $color-black;
            }
            .icon {
                margin-right: 22px;
            }
        }

        &--location {
            max-width: 500px;
        }
    }
</style>
